if (!customElements.get('cart-product-recommendations')) {

  class CartProductRecommendations extends HTMLElement {

    constructor() {
      super();

      this.abortController = null;
      this.cartObserver = null;
      this.reloadTimer = null;
      this.unsubscribeCartUpdate = null;
    }


    connectedCallback() {

      this.content = this.querySelector(
        '[data-cart-recommendations-content]'
      );

      this.row = this.querySelector(
        '[data-cart-recommendations-row]'
      );


      if (!this.content || !this.row) {
        return;
      }


      this.maxProducts =
        parseInt(this.dataset.maxProducts, 10) || 4;


      this.loadRecommendations();

      this.watchCartChanges();

      this.subscribeToDawnCartUpdates();

    }


    disconnectedCallback() {

      if (this.abortController) {
        this.abortController.abort();
      }


      if (this.cartObserver) {
        this.cartObserver.disconnect();
      }


      if (this.reloadTimer) {
        clearTimeout(this.reloadTimer);
      }


      if (
        typeof this.unsubscribeCartUpdate === 'function'
      ) {
        this.unsubscribeCartUpdate();
      }

    }


    getShopifyRoot() {

      return (
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
      ) || '/';

    }


    scheduleReload() {

      if (this.reloadTimer) {
        clearTimeout(this.reloadTimer);
      }


      this.reloadTimer = setTimeout(() => {

        this.loadRecommendations();

      }, 350);

    }


    async getCurrentCart(signal) {

      const root = this.getShopifyRoot();


      const response = await fetch(
        root + 'cart.js',
        {
          signal,
          headers: {
            Accept: 'application/json'
          }
        }
      );


      if (!response.ok) {

        throw new Error(
          `Unable to load cart: ${response.status}`
        );

      }


      return response.json();

    }


    async getRecommendations(
      productId,
      signal
    ) {

      const root = this.getShopifyRoot();


      const url =
        root +
        'recommendations/products.json' +
        '?product_id=' +
        encodeURIComponent(productId) +
        '&limit=10' +
        '&intent=related';


      try {

        const response = await fetch(
          url,
          {
            signal,
            headers: {
              Accept: 'application/json'
            }
          }
        );


        if (!response.ok) {

          throw new Error(
            `Recommendation request failed: ${response.status}`
          );

        }


        const data = await response.json();


        return Array.isArray(data.products)
          ? data.products
          : [];


      } catch (error) {

        if (error.name !== 'AbortError') {

          console.error(
            'Unable to load recommendations for product:',
            productId,
            error
          );

        }


        return [];

      }

    }


    selectRecommendations(
      recommendationGroups,
      cartProductIds
    ) {

      const selected = [];


      /*
       * Put every product currently in the
       * customer's cart into "seen".
       *
       * This prevents cart products from
       * appearing in recommendations.
       */
      const seen = new Set(
        cartProductIds.map(
          (id) => String(id)
        )
      );


      const maxGroupLength = Math.max(
        0,
        ...recommendationGroups.map(
          (group) => group.length
        )
      );


      /*
       * Round-robin selection.
       *
       * Example:
       *
       * Cart Product A:
       * A1 A2 A3
       *
       * Cart Product B:
       * B1 B2 B3
       *
       * Result:
       * A1 B1 A2 B2
       *
       * This prevents one cart product from
       * supplying all recommendations.
       */
      for (
        let index = 0;
        index < maxGroupLength &&
        selected.length < this.maxProducts;
        index++
      ) {

        for (
          const group of recommendationGroups
        ) {

          const product = group[index];


          if (!product) {
            continue;
          }


          const productId =
            String(product.id);


          /*
           * Remove:
           *
           * - product already in cart
           * - duplicate recommendation
           */
          if (seen.has(productId)) {
            continue;
          }


          /*
           * IMPORTANT:
           *
           * Never recommend sold-out products.
           */
          if (!product.available) {

            seen.add(productId);

            continue;

          }


          /*
           * We need a product URL / handle
           * to render the Liquid card.
           */
          if (!product.handle && !product.url) {

            seen.add(productId);

            continue;

          }


          seen.add(productId);

          selected.push(product);


          if (
            selected.length >=
            this.maxProducts
          ) {

            break;

          }

        }

      }


      return selected;

    }


    async renderProductCard(
      product,
      signal
    ) {

      try {

        let productUrl;


        /*
         * Prefer Shopify's recommendation URL
         * because it can contain recommendation
         * tracking parameters.
         */
        if (product.url) {

          productUrl = new URL(
            product.url,
            window.location.origin
          );

        } else {

          const root =
            this.getShopifyRoot();


          productUrl = new URL(
            root +
            'products/' +
            encodeURIComponent(
              product.handle
            ),
            window.location.origin
          );

        }


        /*
         * Ask Shopify to server-render:
         *
         * sections/cart-recommendation-card.liquid
         *
         * inside this product's context.
         */
        productUrl.searchParams.set(
          'section_id',
          'cart-recommendation-card'
        );


        const response = await fetch(
          productUrl.pathname +
          productUrl.search,
          {
            signal
          }
        );


        if (!response.ok) {

          throw new Error(
            `Product card render failed: ${response.status}`
          );

        }


        const html =
          await response.text();


        const container =
          document.createElement('div');


        container.innerHTML = html;


        const card =
          container.querySelector(
            '[data-cart-recommendation-card]'
          );


        return card || null;


      } catch (error) {

        if (error.name !== 'AbortError') {

          console.error(
            'Unable to render recommendation card:',
            product,
            error
          );

        }


        return null;

      }

    }


    async loadRecommendations() {

      if (!this.content || !this.row) {
        return;
      }


      /*
       * Stop an older recommendation request
       * if the cart changes while loading.
       */
      if (this.abortController) {
        this.abortController.abort();
      }


      this.abortController =
        new AbortController();


      const signal =
        this.abortController.signal;


      /*
       * Hide old results while loading
       * the new current-cart recommendations.
       */
      this.content.hidden = true;

      this.row.replaceChildren();


      try {

        /*
         * Always read current cart data.
         *
         * We are not relying on stale Liquid
         * cart data.
         */
        const cart =
          await this.getCurrentCart(signal);


        if (signal.aborted) {
          return;
        }


        if (
          !Array.isArray(cart.items) ||
          cart.items.length === 0
        ) {

          return;

        }


        /*
         * Get unique PRODUCT IDs.
         *
         * Two variants belonging to one product
         * only generate one recommendation API
         * request.
         */
        const cartProductIds = [
          ...new Set(

            cart.items
              .map(
                (item) =>
                  String(item.product_id)
              )
              .filter(Boolean)

          )
        ];


        if (
          cartProductIds.length === 0
        ) {

          return;

        }


        /*
         * Get Shopify Search & Discovery
         * related recommendations for EVERY
         * unique product in the cart.
         */
        const recommendationGroups =
          await Promise.all(

            cartProductIds.map(
              (productId) =>

                this.getRecommendations(
                  productId,
                  signal
                )

            )

          );


        if (signal.aborted) {
          return;
        }


        /*
         * Merge everything and select
         * maximum 4 valid products.
         */
        const recommendedProducts =
          this.selectRecommendations(
            recommendationGroups,
            cartProductIds
          );


        if (
          recommendedProducts.length === 0
        ) {

          return;

        }


        /*
         * Render every recommendation using
         * Shopify Liquid card-product.
         */
        const renderedCards =
          await Promise.all(

            recommendedProducts.map(
              (product) =>

                this.renderProductCard(
                  product,
                  signal
                )

            )

          );


        if (signal.aborted) {
          return;
        }


        const fragment =
          document.createDocumentFragment();


        renderedCards.forEach(
          (card) => {

            if (card) {
              fragment.appendChild(card);
            }

          }
        );


        /*
         * It is possible that Search & Discovery
         * returned products but none survived
         * the final Liquid availability check.
         */
        if (
          !fragment.childNodes.length
        ) {

          return;

        }


        this.row.replaceChildren(
          fragment
        );


        this.content.hidden = false;


      } catch (error) {

        if (error.name !== 'AbortError') {

          console.error(
            'Cart recommendations error:',
            error
          );

        }


        this.content.hidden = true;

      }

    }


    watchCartChanges() {

      /*
       * Dawn full cart page normally contains
       * <cart-items>.
       */
      const cartItems =
        document.querySelector(
          'cart-items'
        ) ||
        document.querySelector(
          '#main-cart-items'
        );


      if (!cartItems) {
        return;
      }


      /*
       * Dawn replaces portions of the cart
       * markup after quantity/remove changes.
       *
       * Observe those changes and then reload
       * recommendations from /cart.js.
       */
      this.cartObserver =
        new MutationObserver(() => {

          this.scheduleReload();

        });


      this.cartObserver.observe(
        cartItems,
        {
          childList: true,
          subtree: true
        }
      );

    }


    subscribeToDawnCartUpdates() {

      /*
       * Newer Dawn versions publish cart-update
       * events.
       *
       * Feature detection means this code still
       * works if your Dawn version doesn't.
       */
      try {

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {

          this.unsubscribeCartUpdate =
            subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              () => {

                this.scheduleReload();

              }
            );

        }

      } catch (error) {

        /*
         * MutationObserver above remains the
         * fallback, so no action is required.
         */

      }

    }

  }


  customElements.define(
    'cart-product-recommendations',
    CartProductRecommendations
  );

}